// frontend/src/components/LeaderboardTab.jsx
import React, { useEffect, useState } from 'react';
import { Crown } from 'lucide-react';
import { api } from '../api';
import { colors, fontFamily } from '../theme';

// Local copy of the currency-symbol helper (not exported from a shared location — see CustomerView.jsx).
const CC = ({ size = '1em' }) => (
  <span style={{ fontSize: size, marginLeft: 4, verticalAlign: 'baseline' }}>₵₵</span>
);

const shimmerKeyframes = `
@keyframes leaderboardShimmer {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}`;

function BalanceCell({ balance }) {
  const color = balance >= 0 ? colors.odds : colors.negative;
  const sign = balance >= 0 ? '+' : '';
  return (
    <span style={{ color, fontWeight: 700 }}>
      {sign}{balance.toFixed(2)}<CC />
    </span>
  );
}

function RankRow({ entry, isLiveLeader, isChampion, right }) {
  const rowStyle = {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '0.75rem 1rem', borderRadius: 8, marginBottom: '0.5rem',
    background: colors.cardBg, boxShadow: colors.shadowCard,
    border: isChampion ? `1px solid ${colors.gold}` : '1px solid transparent',
  };
  if (isChampion) {
    rowStyle.boxShadow = `${colors.shadowCard}, 0 0 12px ${colors.gold}`;
  }
  if (isLiveLeader) {
    rowStyle.backgroundImage = `linear-gradient(90deg, ${colors.cardBg}, rgba(191,149,63,0.35), ${colors.cardBg})`;
    rowStyle.backgroundSize = '200% 100%';
    rowStyle.animation = 'leaderboardShimmer 4s linear infinite';
  }

  return (
    <div style={rowStyle}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
        <span style={{ color: colors.textMuted, fontWeight: 700, minWidth: 20 }}>#{entry.rank}</span>
        {isChampion && <Crown size={16} color={colors.gold} data-testid={`crown-icon-${entry.customer_id}`} />}
        <span style={{ color: isChampion ? colors.gold : colors.textPrimary, fontWeight: 600 }}>{entry.name}</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <BalanceCell balance={entry.balance} />
        {right}
      </div>
    </div>
  );
}

function ThisWeekView({ entries, championId }) {
  if (entries.length === 0) {
    return <p style={{ color: colors.textMuted, textAlign: 'center', padding: '2rem 0' }}>No one's bet yet this week.</p>;
  }
  return (
    <div>
      {entries.map(entry => (
        <RankRow
          key={entry.customer_id}
          entry={entry}
          isLiveLeader={entry.rank === 1}
          isChampion={entry.customer_id === championId}
        />
      ))}
    </div>
  );
}

function CustomerBetDetail({ bets, parlays, casino }) {
  if (bets.length === 0 && parlays.length === 0 && casino.length === 0) {
    return <p style={{ color: colors.textMuted, fontSize: '0.85rem', padding: '0.5rem 0' }}>No bets recorded.</p>;
  }
  return (
    <div style={{ padding: '0.5rem 0' }}>
      {bets.map(b => (
        <div key={b.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.35rem 0', borderBottom: `1px solid ${colors.sectionBg}`, fontSize: '0.82rem' }}>
          <span style={{ color: colors.textPrimary }}>{b.description}</span>
          <span style={{ color: colors.textMuted }}>{b.odds} · {b.amount.toFixed(2)}<CC size="0.9em" /> · {b.status}</span>
        </div>
      ))}
      {parlays.map(p => (
        <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.35rem 0', borderBottom: `1px solid ${colors.sectionBg}`, fontSize: '0.82rem' }}>
          <span style={{ color: colors.freePlay }}>{p.num_legs}-leg parlay</span>
          <span style={{ color: colors.textMuted }}>{p.combined_odds} · {p.amount.toFixed(2)}<CC size="0.9em" /> · {p.status}</span>
        </div>
      ))}
      {casino.map(c => (
        <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.35rem 0', borderBottom: `1px solid ${colors.sectionBg}`, fontSize: '0.82rem' }}>
          <span style={{ color: colors.textPrimary }}>
            {c.description}{c.round_count > 1 && <span style={{ color: colors.textPrimary }}> · {c.round_count} rounds</span>}
          </span>
          <span style={{ color: colors.textMuted }}>{c.amount.toFixed(2)}<CC size="0.9em" /> · {c.status}</span>
        </div>
      ))}
    </div>
  );
}

function LastWeekRow({ entry }) {
  const [expanded, setExpanded] = useState(false);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState(false);

  const toggle = async () => {
    if (!expanded && !detail) {
      setLoadingDetail(true);
      try {
        const data = await api.leaderboard.getCustomerBets(entry.customer_id);
        setDetail(data);
        setDetailError(false);
      } catch {
        setDetailError(true);
      } finally {
        setLoadingDetail(false);
      }
    }
    setExpanded(e => !e);
  };

  return (
    <div style={{ marginBottom: '0.5rem' }}>
      <RankRow
        entry={entry}
        isLiveLeader={false}
        isChampion={entry.rank === 1}
        right={
          <button
            onClick={toggle}
            style={{ padding: '0.3rem 0.7rem', fontSize: '0.78rem', borderRadius: 6, border: `1px solid ${colors.goldMuted}`, background: 'transparent', color: colors.textPrimary, cursor: 'pointer', fontFamily }}
          >
            {expanded ? 'Hide' : 'Details'}
          </button>
        }
      />
      {expanded && (
        <div style={{ background: colors.sectionBg, borderRadius: 8, padding: '0.5rem 1rem', marginTop: '-0.25rem', marginBottom: '0.5rem' }}>
          {loadingDetail ? (
            <p style={{ color: colors.textMuted, fontSize: '0.85rem' }}>Loading…</p>
          ) : detailError ? (
            <p style={{ color: colors.negative, fontSize: '0.85rem' }}>Couldn't load bet details — try again.</p>
          ) : (
            <CustomerBetDetail bets={detail?.bets || []} parlays={detail?.parlays || []} casino={detail?.casino || []} />
          )}
        </div>
      )}
    </div>
  );
}

function LastWeekView({ period, entries }) {
  if (!period) {
    return <p style={{ color: colors.textMuted, textAlign: 'center', padding: '2rem 0' }}>No leaderboard yet — check back after this week ends.</p>;
  }
  if (entries.length === 0) {
    return <p style={{ color: colors.textMuted, textAlign: 'center', padding: '2rem 0' }}>No one bet last week.</p>;
  }
  return (
    <div>
      {entries.map(entry => <LastWeekRow key={entry.customer_id} entry={entry} />)}
    </div>
  );
}

export default function LeaderboardTab() {
  const [tab, setTab] = useState('current');
  const [current, setCurrent] = useState(null);
  const [lastWeek, setLastWeek] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.all([api.leaderboard.getCurrent(), api.leaderboard.getLastWeek()])
      .then(([currentData, lastWeekData]) => {
        if (cancelled) return;
        setCurrent(currentData);
        setLastWeek(lastWeekData);
        setError(false);
      })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return <p style={{ color: colors.textMuted, textAlign: 'center', padding: '2rem 0', fontFamily }}>Loading leaderboard…</p>;
  }

  if (error) {
    return <p style={{ color: colors.negative, textAlign: 'center', padding: '2rem 0', fontFamily }}>Couldn't load the leaderboard — try again.</p>;
  }

  const championId = current?.reigning_champion?.customer_id ?? null;

  return (
    <div style={{ padding: '1rem', fontFamily }}>
      <style>{shimmerKeyframes}</style>
      <h2 style={{ color: colors.textPrimary, marginBottom: '1rem' }}>Leaderboard</h2>

      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
        {[['current', 'This Week'], ['lastWeek', 'Last Week']].map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            style={{
              padding: '0.5rem 1rem', borderRadius: 20, border: 'none', cursor: 'pointer', fontFamily,
              background: tab === key ? colors.goldActiveBg : colors.sectionBg,
              color: tab === key ? colors.gold : colors.textMuted, fontWeight: tab === key ? 700 : 400,
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'current' && <ThisWeekView entries={current?.entries || []} championId={championId} />}
      {tab === 'lastWeek' && <LastWeekView period={lastWeek?.period} entries={lastWeek?.entries || []} />}
    </div>
  );
}
